import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { fetchSpend, type SpendReport } from '@/lib/adminApi';
import { canReadSpend } from '@/lib/roles';
import {
  DEFAULT_SCENARIOS,
  deriveBase,
  forecast,
  sumCost,
  teamCostOf,
  type ForecastScenario,
} from '@/lib/spendForecast';
import { useMe } from '@/lib/useMe';

const WINDOWS = [3, 6, 12] as const;
const TOP_ACCOUNTS = 5;
const OTHER = 'other';
const TEST_ACCOUNTS = 'test accounts';

/** One fill / stroke pair per series. Literal class names so Tailwind sees them. */
const SERIES = [
  { bg: 'bg-primary', stroke: 'stroke-primary' },
  { bg: 'bg-note-blue-edge', stroke: 'stroke-note-blue-edge' },
  { bg: 'bg-note-green-edge', stroke: 'stroke-note-green-edge' },
  { bg: 'bg-note-yellow-edge', stroke: 'stroke-note-yellow-edge' },
  { bg: 'bg-note-violet-edge', stroke: 'stroke-note-violet-edge' },
  { bg: 'bg-fg-faint', stroke: 'stroke-fg-faint' },
] as const;

function usd(value: number): string {
  return `$${value.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

function usd2(value: number): string {
  return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function num(value: number): string {
  return Math.round(value).toLocaleString('en-US');
}

function pct(share: number | null): string {
  return share === null ? '-' : `${(share * 100).toFixed(1)}%`;
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function monthName(month: string): string {
  return MONTH_NAMES[Number(month.slice(5, 7)) - 1] ?? month;
}

/** Is the report's current month still running at `as_of`? */
function currentMonthState(report: SpendReport): { month: string; partial: boolean; to: string } {
  const at = new Date(report.as_of);
  const month = `${at.getUTCFullYear()}-${String(at.getUTCMonth() + 1).padStart(2, '0')}`;
  const last = new Date(Date.UTC(at.getUTCFullYear(), at.getUTCMonth() + 1, 0)).getUTCDate();
  return {
    month,
    partial: at.getUTCDate() < last,
    to: `${monthName(month)} ${at.getUTCDate()}`,
  };
}

/**
 * Spend (ClickUp 1amz0xe2evr): what generations cost US — provider cost
 * (`real_cost_cents`), not the price a user paid — for the team and for users,
 * with a 6-month forecast over editable scenarios. Open to every console role
 * (`viewer` and up); the API enforces the same line. Users are aggregates only:
 * the body carries no user email, id or workflow title, so neither does this
 * page.
 */
export function SpendPage() {
  const { data: me } = useMe();
  const [months, setMonths] = useState<number>(6);
  const allowed = canReadSpend(me?.role);
  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin-reports', 'spend', months],
    queryFn: () => fetchSpend(months),
    enabled: allowed,
    retry: false,
  });

  if (!allowed) {
    return (
      <div>
        <h1 className="text-h2 font-semibold text-fg-primary">Spend</h1>
        <p className="mt-2 text-body text-fg-secondary">You need a console role to see the spend report.</p>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-h2 font-semibold text-fg-primary">Spend</h1>
      <p className="mt-1 text-body text-fg-secondary">
        What generations cost us (provider cost) — the team and users, month by month, and a forecast.
      </p>

      <div className="mt-4 flex items-center gap-2" role="group" aria-label="Window">
        {WINDOWS.map((w) => (
          <button
            key={w}
            type="button"
            onClick={() => setMonths(w)}
            aria-pressed={months === w}
            className={`rounded-card border px-3 py-1 text-meta ${
              months === w
                ? 'border-primary bg-surface-brand-subtle text-fg-primary'
                : 'border-border text-fg-secondary hover:bg-state-hover'
            }`}
          >
            Last {w} months
          </button>
        ))}
      </div>

      {isLoading && <p className="mt-4 text-body text-fg-hint">Loading…</p>}
      {isError && <p className="mt-4 text-body text-destructive">Could not load the report. Try again.</p>}

      {data && (
        <div className="mt-6 flex flex-col gap-6">
          <Tiles report={data} />
          <TeamSection report={data} />
          <UsersSection report={data} />
          <ForecastSection report={data} />
          <HowCounted />
        </div>
      )}
    </div>
  );
}

function Card({
  title,
  children,
  testId,
}: {
  title: string;
  children: React.ReactNode;
  testId?: string;
}) {
  return (
    <section className="rounded-card border border-border p-4" data-testid={testId}>
      <h2 className="text-h3 font-semibold text-fg-primary">{title}</h2>
      {children}
    </section>
  );
}

function Tiles({ report }: { report: SpendReport }) {
  const state = currentMonthState(report);
  const latest = report.users_monthly[report.users_monthly.length - 1];
  const prev = report.users_monthly[report.users_monthly.length - 2];
  if (!latest) return <p className="text-body text-fg-hint">No data in this window.</p>;
  const label = latest.month === state.month && state.partial ? `${latest.month}, to ${state.to}` : latest.month;
  const tiles: { key: string; title: string; value: string; hint: string }[] = [
    { key: 'team', title: 'Team cost', value: usd(teamCostOf(report, latest.month)), hint: label },
    { key: 'users', title: 'Users cost', value: usd(latest.cost_usd), hint: label },
    {
      key: 'active',
      title: 'Active users',
      value: num(latest.active_users),
      hint: `of ${num(latest.signups)} signups, ${label}`,
    },
    {
      key: 'retention',
      title: 'Retention',
      value: prev ? pct(prev.retained_next_month) : '-',
      hint: prev ? `${monthName(prev.month)} to ${monthName(latest.month)}` : 'needs two months',
    },
  ];
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="spend-tiles">
      {tiles.map((t) => (
        <div key={t.key} className="rounded-card border border-border p-4" data-testid={`spend-tile-${t.key}`}>
          <p className="text-meta text-fg-secondary">{t.title}</p>
          <p className="mt-1 text-h2 font-semibold text-fg-primary">{t.value}</p>
          <p className="mt-1 text-micro text-fg-hint">{t.hint}</p>
        </div>
      ))}
    </div>
  );
}

function Legend({ names }: { names: string[] }) {
  return (
    <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
      {names.map((name, i) => (
        <li key={name} className="flex items-center gap-1 text-micro text-fg-secondary">
          <span className={`inline-block h-2 w-2 rounded-full ${SERIES[i % SERIES.length].bg}`} aria-hidden="true" />
          {name}
        </li>
      ))}
    </ul>
  );
}

function TeamSection({ report }: { report: SpendReport }) {
  const monthList = report.users_monthly.map((r) => r.month);
  const { accounts, byMonth, max } = useMemo(() => {
    const totals = new Map<string, number>();
    for (const r of report.team_monthly) totals.set(r.account, (totals.get(r.account) ?? 0) + r.cost_usd);
    const ranked = [...totals.entries()].sort((a, b) => b[1] - a[1]).map(([a]) => a);
    const top = ranked.slice(0, TOP_ACCOUNTS);
    const hasOther = ranked.length > TOP_ACCOUNTS;
    const names = hasOther ? [...top, OTHER] : top;
    const grid = new Map<string, Map<string, number>>();
    for (const r of report.team_monthly) {
      const col = top.includes(r.account) ? r.account : OTHER;
      const m = grid.get(r.month) ?? new Map<string, number>();
      m.set(col, (m.get(col) ?? 0) + r.cost_usd);
      grid.set(r.month, m);
    }
    let peak = 0;
    for (const m of grid.values()) peak = Math.max(peak, [...m.values()].reduce((a, b) => a + b, 0));
    return { accounts: names, byMonth: grid, max: peak || 1 };
  }, [report]);

  const kinds = useMemo(() => {
    const acc = new Map<string, { teamOps: number; teamCost: number; userOps: number; userCost: number }>();
    for (const r of report.by_kind) {
      if (r.segment === 'test') continue;
      const row = acc.get(r.kind) ?? { teamOps: 0, teamCost: 0, userOps: 0, userCost: 0 };
      if (r.segment === 'team') {
        row.teamOps += r.ops;
        row.teamCost += r.cost_usd;
      } else {
        row.userOps += r.ops;
        row.userCost += r.cost_usd;
      }
      acc.set(r.kind, row);
    }
    return [...acc.entries()];
  }, [report]);

  return (
    <Card title="Team" testId="spend-team">
      {accounts.length === 0 ? (
        <p className="mt-2 text-body text-fg-hint">No team spend in this window.</p>
      ) : (
        <>
          <div className="mt-3 flex h-40 items-end gap-3" role="img" aria-label="Team cost per month by account">
            {monthList.map((month) => {
              const cols = byMonth.get(month) ?? new Map<string, number>();
              const total = [...cols.values()].reduce((a, b) => a + b, 0);
              return (
                <div key={month} className="flex h-full min-w-0 flex-1 flex-col justify-end">
                  <span className="text-center text-micro text-fg-secondary">{usd(total)}</span>
                  <div className="flex w-full flex-col-reverse overflow-hidden rounded-card" style={{ height: `${(total / max) * 100}%` }}>
                    {accounts.map((a, i) => (
                      <div
                        key={a}
                        className={SERIES[i % SERIES.length].bg}
                        style={{ height: `${total ? ((cols.get(a) ?? 0) / total) * 100 : 0}%` }}
                        title={`${a}: ${usd2(cols.get(a) ?? 0)}`}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-1 flex gap-3">
            {monthList.map((month) => (
              <span key={month} className="min-w-0 flex-1 text-center text-micro text-fg-hint">
                {monthName(month)}
              </span>
            ))}
          </div>
          <Legend names={accounts.map((a) => (a === TEST_ACCOUNTS ? 'test accounts' : a))} />
        </>
      )}

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <div>
          <h3 className="text-body font-semibold text-fg-primary">Top workflows</h3>
          <table className="mt-2 w-full text-left text-meta" data-testid="spend-team-workflows">
            <thead className="text-fg-secondary">
              <tr>
                <th className="py-1 font-medium">Account</th>
                <th className="py-1 font-medium">Workflow</th>
                <th className="py-1 text-right font-medium">Ops</th>
                <th className="py-1 text-right font-medium">Cost</th>
              </tr>
            </thead>
            <tbody>
              {report.team_by_workflow.map((r) => (
                <tr key={`${r.account}|${r.workflow}`} className="border-t border-border">
                  <td className="py-1 text-fg-secondary">{r.account}</td>
                  <td className="py-1 text-fg-primary">{r.workflow}</td>
                  <td className="py-1 text-right">{num(r.ops)}</td>
                  <td className="py-1 text-right">{usd2(r.cost_usd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div>
          <h3 className="text-body font-semibold text-fg-primary">By kind</h3>
          <table className="mt-2 w-full text-left text-meta" data-testid="spend-by-kind">
            <thead className="text-fg-secondary">
              <tr>
                <th className="py-1 font-medium">Kind</th>
                <th className="py-1 text-right font-medium">Team ops</th>
                <th className="py-1 text-right font-medium">Team cost</th>
                <th className="py-1 text-right font-medium">Users ops</th>
                <th className="py-1 text-right font-medium">Users cost</th>
              </tr>
            </thead>
            <tbody>
              {kinds.map(([kind, r]) => (
                <tr key={kind} className="border-t border-border">
                  <td className="py-1 text-fg-primary">{kind}</td>
                  <td className="py-1 text-right">{num(r.teamOps)}</td>
                  <td className="py-1 text-right">{usd2(r.teamCost)}</td>
                  <td className="py-1 text-right">{num(r.userOps)}</td>
                  <td className="py-1 text-right">{usd2(r.userCost)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Card>
  );
}

function UsersSection({ report }: { report: SpendReport }) {
  const max = report.users_monthly.reduce((m, r) => Math.max(m, r.cost_usd), 0) || 1;
  const latestMonth = report.users_monthly[report.users_monthly.length - 1]?.month;
  const tiers = report.user_tiers.filter((t) => t.month === latestMonth);
  return (
    <Card title="Users" testId="spend-users">
      <ol className="mt-3 flex flex-col gap-2">
        {report.users_monthly.map((r) => (
          <li key={r.month} className="text-body">
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-fg-primary">{r.month}</span>
              <span className="shrink-0 text-meta text-fg-secondary">
                {usd(r.cost_usd)} · {num(r.active_users)} active · {num(r.signups)} signups · retained{' '}
                {pct(r.retained_next_month)}
              </span>
            </div>
            <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-surface-muted">
              <div
                className="h-full rounded-full bg-primary"
                style={{ width: `${Math.max(2, (r.cost_usd / max) * 100)}%` }}
                aria-hidden="true"
              />
            </div>
          </li>
        ))}
      </ol>

      <h3 className="mt-6 text-body font-semibold text-fg-primary">Tiers, {latestMonth}</h3>
      <table className="mt-2 w-full text-left text-meta" data-testid="spend-tiers">
        <thead className="text-fg-secondary">
          <tr>
            <th className="py-1 font-medium">Tier</th>
            <th className="py-1 text-right font-medium">Users</th>
            <th className="py-1 text-right font-medium">Cost</th>
            <th className="py-1 text-right font-medium">Cost / user</th>
            <th className="py-1 text-right font-medium">Ops / user</th>
          </tr>
        </thead>
        <tbody>
          {tiers.map((t) => (
            <tr key={t.tier} className="border-t border-border">
              <td className="py-1 text-fg-primary">
                {t.tier} <span className="text-fg-hint">{TIER_RANGE[t.tier]}</span>
              </td>
              <td className="py-1 text-right">{num(t.users)}</td>
              <td className="py-1 text-right">{usd2(t.cost_usd)}</td>
              <td className="py-1 text-right">{usd2(t.cost_per_user)}</td>
              <td className="py-1 text-right">{t.ops_per_user.toFixed(1)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="mt-3 text-micro text-fg-hint">
        A tier is the number of charges a user made in the month. Users are counted, never named.
      </p>
    </Card>
  );
}

const TIER_RANGE = { casual: '1-20 charges', regular: '21-100', power: '101+' } as const;

type NumericField = Exclude<keyof ForecastScenario, 'id' | 'name'>;

const FIELDS: { key: NumericField; label: string }[] = [
  { key: 'signupGrowth', label: 'Signup growth / mo' },
  { key: 'activation', label: 'Activation' },
  { key: 'retention', label: 'Retention' },
  { key: 'regularShare', label: 'Regular share' },
  { key: 'powerShare', label: 'Power share' },
  { key: 'teamGrowth', label: 'Team growth / mo' },
];

function ForecastSection({ report }: { report: SpendReport }) {
  const [scenarios, setScenarios] = useState<ForecastScenario[]>(() => DEFAULT_SCENARIOS.map((s) => ({ ...s })));
  const base = useMemo(() => deriveBase(report), [report]);
  const runs = useMemo(
    () => (base ? scenarios.map((s) => ({ scenario: s, months: forecast(base, s) })) : []),
    [base, scenarios],
  );

  if (!base) {
    return (
      <Card title="Forecast, 6 months" testId="spend-forecast">
        <p className="mt-2 text-body text-fg-hint">Not enough data to forecast: no month with active users.</p>
      </Card>
    );
  }

  const setField = (id: string, key: NumericField, raw: string) => {
    const value = Number(raw);
    setScenarios((prev) =>
      prev.map((s) => (s.id === id ? { ...s, [key]: Number.isFinite(value) ? value / 100 : 0 } : s)),
    );
  };

  return (
    <Card title="Forecast, 6 months" testId="spend-forecast">
      <p className="mt-1 text-meta text-fg-secondary" data-testid="spend-forecast-base">
        Base: {base.scaled ? `${base.month}, still running, scaled to a full month` : `${base.month}, the last full month`}
        {' '}({num(base.signups)} signups, {num(base.activeUsers)} active users, team {usd(base.teamCost)}).
      </p>

      <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {scenarios.map((s) => (
          <fieldset key={s.id} className="rounded-card border border-border p-3" data-testid={`spend-scenario-${s.id}`}>
            <legend className="px-1 text-meta font-semibold text-fg-primary">{s.name}</legend>
            {FIELDS.map((f) => (
              <label key={f.key} className="mt-1 flex items-center justify-between gap-2 text-micro text-fg-secondary">
                {f.label}
                <span className="flex items-center gap-1">
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    aria-label={`${s.name} ${f.label}`}
                    value={Number((s[f.key] * 100).toFixed(2))}
                    onChange={(e) => setField(s.id, f.key, e.target.value)}
                    className="w-16 rounded-card border border-border px-1 py-0.5 text-right text-meta text-fg-primary"
                  />
                  %
                </span>
              </label>
            ))}
          </fieldset>
        ))}
      </div>
      <button
        type="button"
        onClick={() => setScenarios(DEFAULT_SCENARIOS.map((s) => ({ ...s })))}
        className="mt-3 rounded-card border border-border px-3 py-1 text-meta text-fg-secondary hover:bg-state-hover"
      >
        Reset scenarios
      </button>

      <table className="mt-6 w-full text-left text-meta" data-testid="spend-forecast-summary">
        <thead className="text-fg-secondary">
          <tr>
            <th className="py-1 font-medium">Scenario</th>
            <th className="py-1 text-right font-medium">
              {monthName(runs[0].months[0].month)} - {monthName(runs[0].months[runs[0].months.length - 1].month)}
            </th>
            <th className="py-1 text-right font-medium">Last month</th>
            <th className="py-1 text-right font-medium">6-month sum</th>
            <th className="py-1 text-right font-medium">Active users at the end</th>
          </tr>
        </thead>
        <tbody>
          {runs.map(({ scenario, months: ms }, i) => (
            <tr key={scenario.id} className="border-t border-border">
              <td className="py-1 text-fg-primary">
                <span className={`mr-2 inline-block h-2 w-2 rounded-full ${SERIES[i].bg}`} aria-hidden="true" />
                {scenario.name}
              </td>
              <td className="py-1 text-right">{ms.map((m) => usd(m.totalCost)).join(' · ')}</td>
              <td className="py-1 text-right">{usd(ms[ms.length - 1].totalCost)}</td>
              <td className="py-1 text-right font-semibold">{usd(sumCost(ms))}</td>
              <td className="py-1 text-right">{num(ms[ms.length - 1].activeUsers)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <LineChart runs={runs} />
    </Card>
  );
}

function LineChart({ runs }: { runs: { scenario: ForecastScenario; months: { month: string; totalCost: number }[] }[] }) {
  const W = 600;
  const H = 200;
  const PAD = 8;
  const n = runs[0].months.length;
  const peak = Math.max(1, ...runs.flatMap((r) => r.months.map((m) => m.totalCost)));
  const x = (i: number) => PAD + (n === 1 ? 0 : (i / (n - 1)) * (W - 2 * PAD));
  const y = (v: number) => H - PAD - (v / peak) * (H - 2 * PAD);
  return (
    <div className="mt-4">
      <div className="flex gap-2">
        <span className="w-12 shrink-0 text-micro text-fg-hint">{usd(peak)}</span>
        <svg
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="none"
          className="min-w-0 flex-1"
          style={{ height: 200 }}
          role="img"
          aria-label="Total monthly cost per scenario"
        >
          <line x1={PAD} y1={H - PAD} x2={W - PAD} y2={H - PAD} className="stroke-border" strokeWidth="1" vectorEffect="non-scaling-stroke" />
          {runs.map(({ scenario, months: ms }, i) => (
            <polyline
              key={scenario.id}
              fill="none"
              strokeWidth="2"
              vectorEffect="non-scaling-stroke"
              className={SERIES[i].stroke}
              points={ms.map((m, j) => `${x(j)},${y(m.totalCost)}`).join(' ')}
            />
          ))}
        </svg>
      </div>
      <div className="ml-14 flex justify-between">
        {runs[0].months.map((m) => (
          <span key={m.month} className="text-micro text-fg-hint">
            {monthName(m.month)}
          </span>
        ))}
      </div>
      <Legend names={runs.map((r) => r.scenario.name)} />
    </div>
  );
}

function HowCounted() {
  return (
    <section className="rounded-card border border-border p-4" data-testid="spend-how">
      <h2 className="text-h3 font-semibold text-fg-primary">How it is counted</h2>
      <ul className="mt-2 list-disc pl-5 text-meta text-fg-secondary">
        <li>Cost is the provider cost to us (real_cost_cents of each charge), not the price a user paid in credits.</li>
        <li>One op is one charge row in the credit ledger; grants, purchases and adjustments are not counted.</li>
        <li>
          Team: accounts in the platform's internal organizations, or on a holywater.tech, fastframe.tech or
          fastfrm.com email. Fixture and mail-simulator domains (TEST_EMAIL_DOMAINS in the API) are
          shown as "test accounts" and left out of the team figures above. Everyone else is a user.
        </li>
        <li>The current month is partial; its figures are marked "to" a date and grow until the month ends.</li>
      </ul>
    </section>
  );
}
